import { IconSparkles, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isGeneratePool, type KeywordPool } from "./model";

const primary = "h-8 rounded-lg px-3 text-xs";
const secondary = "h-8 rounded-lg border-line bg-surface px-3 text-xs text-ink";

/** Stacked sheet with a brand badge (TP-02a / TP-02). */
function SheetIllustration({ icon: Icon }: { icon: typeof IconSparkles }) {
  return (
    <div aria-hidden className="relative h-[72px] w-28 shrink-0">
      <div className="absolute top-[18px] left-2 h-11 w-16 rounded-lg border border-line bg-subtle" />
      <div className="absolute top-2 left-6 flex h-11 w-16 flex-col gap-1.5 rounded-lg border border-line bg-surface p-2.5 shadow-[0_4px_12px_#10182814]">
        <span className="h-[5px] w-9 shrink-0 rounded-[3px] bg-gray-300" />
        <span className="h-[5px] w-7 shrink-0 rounded-[3px] bg-gray-200" />
        <span className="h-[5px] w-10 shrink-0 rounded-[3px] bg-gray-200" />
      </div>
      <div className="absolute top-9 left-[76px] flex size-7 items-center justify-center rounded-full bg-primary shadow-[0_0_0_4px_var(--color-surface)]">
        <Icon className="size-3.5 text-primary-foreground" />
      </div>
    </div>
  );
}

/** TP-08 小语种: EN card + dashed ES card. */
function LanguageIllustration() {
  return (
    <div aria-hidden className="relative h-[72px] w-28 shrink-0">
      <div className="absolute top-3.5 left-1.5 flex h-10 w-[58px] items-center justify-center rounded-[10px] border border-line bg-subtle text-[13px] font-bold leading-[18px] text-muted-foreground">
        EN
      </div>
      <div className="absolute top-[26px] left-11 flex h-10 w-[58px] items-center justify-center rounded-[10px] border-[1.5px] border-dashed border-control-border-hover bg-surface text-[13px] font-bold leading-[18px] text-primary">
        ES
      </div>
    </div>
  );
}

function Frame({
  card,
  illustration,
  title,
  description,
  children,
}: {
  card?: boolean;
  illustration: React.ReactNode;
  title: string;
  description?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 px-6",
        card ? "rounded-[14px] border border-line bg-surface py-12" : "py-10",
      )}
    >
      {illustration}
      <span className="text-sm font-semibold text-ink">{title}</span>
      {description ? (
        <span className="text-center text-xs leading-[18px] text-muted-foreground">
          {description}
        </span>
      ) : null}
      {children ? <div className="flex gap-2 pt-1">{children}</div> : null}
    </div>
  );
}

/** TP-02a: nothing imported yet. */
export function StartEmptyState({ onUpload, onAdd }: { onUpload: () => void; onAdd: () => void }) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <Frame illustration={<SheetIllustration icon={IconUpload} />} title={t("keywords.empty.start")}>
      <Button className={primary} onClick={onUpload}>
        {t("keywords.empty.upload")}
      </Button>
      <Button variant="outline" className={secondary} onClick={onAdd}>
        {t("keywords.empty.addManual")}
      </Button>
    </Frame>
  );
}

/** TP-02: imported, not classified yet. */
export function UnclassifiedEmptyState({
  onClassify,
  onAdd,
}: {
  onClassify: () => void;
  onAdd: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <Frame
      illustration={<SheetIllustration icon={IconSparkles} />}
      title={t("keywords.empty.unclassified")}
      description={t("keywords.empty.unclassifiedDescription")}
    >
      <Button className={primary} onClick={onClassify}>
        {t("keywords.classify.action")}
      </Button>
      <Button variant="outline" className={secondary} onClick={onAdd}>
        {t("keywords.empty.addManual")}
      </Button>
    </Frame>
  );
}

/** TP-08: one pool is empty (guidance + CTA only, Q10). */
export function PoolEmptyState({
  pool,
  onGenerate,
  onAdd,
}: {
  pool: KeywordPool;
  /** Omitted where keyword generation is unavailable (a demo deployment). */
  onGenerate?: () => void;
  onAdd: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <Frame
      card
      illustration={
        pool === "non_english" ? (
          <LanguageIllustration />
        ) : (
          <SheetIllustration
            icon={isGeneratePool(pool) && onGenerate ? IconSparkles : IconUpload}
          />
        )
      }
      title={t("keywords.empty.pool", { pool: t(`keywords.pools.${pool}`) })}
      description={t(`keywords.empty.poolDescription.${pool}`)}
    >
      {isGeneratePool(pool) && onGenerate ? (
        <Button className={primary} onClick={onGenerate}>
          <IconSparkles className="size-3.5" aria-hidden />
          {t("keywords.toolbar.generate")}
        </Button>
      ) : null}
      {pool === "discard" ? null : (
        <Button variant="outline" className={secondary} onClick={onAdd}>
          {t("keywords.empty.add")}
        </Button>
      )}
    </Frame>
  );
}

export function SearchEmptyState({ query }: { query: string }) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <div className="rounded-[14px] border border-line bg-surface px-6 py-12 text-center text-xs text-muted-foreground">
      {t("keywords.empty.search", { query })}
    </div>
  );
}
